import area from "@turf/area";
import bbox from "@turf/bbox";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import distance from "@turf/distance";
import { feature as asFeature, featureCollection } from "@turf/helpers";
import intersect from "@turf/intersect";
import type { BBox, Feature, LineString, MultiLineString, MultiPolygon, Point, Polygon } from "geojson";
import makeIndex from "geojson-rbush";

import {
  computeStreetParcelFeatures,
  DEFAULT_STREET_PARCEL_CONFIG,
  StreetBuffers,
  type StreetGeometry,
  type StreetParcelConfig,
  type StreetParcelFeatures,
} from "./features";
import { type CompactModel, predictProbability } from "./model";
import { expandBboxDegrees, expandBboxMetres, lineWithinDegrees, lineWithinMetres, nearestLineWithinMetres, pointWithinMetres } from "./proximity";

/**
 * Classification of a parcel layer against a street layer and demand points.
 *
 * The gate and nearby radii are planar distances in degrees, to match the training pipeline. All other distances are
 * in metres.
 */

export interface ParcelLayerItem {
  id: number | string;
  geometry: Polygon | MultiPolygon;
  addressType: string | null;
}

export interface StreetLayerItem {
  /**
   * The geometry of the street.
   */
  geometry: StreetGeometry;

  /**
   * The OSM "highway" value for this street.
   *
   * "service" streets are generally excluded from consideration.
   */
  highway: string | null;
}

export interface DemandPoint {
  /** A unique identifier of the demand. */
  id: number | string;

  /** The geometry of the demand. */
  geometry: Point;
}

export interface LayerClassificationConfig extends StreetParcelConfig {
  /** A parcel is a candidate only if a non-service street lies within this many degrees of it. */
  gateDistanceDegrees: number;

  /**
   * Candidates whose street-overlap ratio are below this value are ignored, unless they contain at least
   * `minCenterlineLengthMetres` of street centreline.
   */
  minStreetOverlapRatio: number;

  /** Candidates containing at least this many metres of street centreline are kept regardless of their overlap ratio. */
  minCenterlineLengthMetres: number;

  /** Radius (degrees) for gathering the streets used by centreline length and nearest-street. */
  nearbyRadiusDegrees: number;

  /** Parcels at least this far (metres) from every street, and containing no demand, are distant. */
  distantDistanceMetres: number;

  /** How far (metres) to look for a distant parcel's nearest street; beyond this its distance is reported as `null`. */
  nearestStreetSearchMetres: number;

  /**
   * Radius (metres) of the street buffer that defines the design's range.
   *
   * Parcels extending beyond the range are clipped to it before classification.
   */
  rangeBufferMetres: number;

  /** `highway` value treated as a service road and excluded from the gate/overlap. */
  serviceHighwayValue: string;

  /**
   * Parcels at/above this probability have their demand membership computed and are considered
   * results. Defaults to the model's own threshold when unset (i.e. only street parcels).
   */
  minProbability?: number;
}

export const DEFAULT_LAYER_CLASSIFICATION_CONFIG: LayerClassificationConfig = {
  ...DEFAULT_STREET_PARCEL_CONFIG,
  gateDistanceDegrees: 0.00015,
  minStreetOverlapRatio: 0.1,
  minCenterlineLengthMetres: 40,
  nearbyRadiusDegrees: 0.001,
  distantDistanceMetres: 50,
  nearestStreetSearchMetres: 500,
  rangeBufferMetres: 100,
  serviceHighwayValue: "service",
};

/** How a parcel was classified. */
export type ClassificationKind =
  /** No street lies within `distantDistanceMetres` of the parcel and no demand lies inside it; the model was not run. */
  | "distant"
  /** The parcel is not a candidate (no gate street, or too little street overlap and centreline). */
  | "excluded"
  /** The model was run on the parcel's geometry. */
  | "model"
  /** The model was run on the parcel's geometry clipped to the design's range. */
  | "clipped";

interface ClassificationResultBase {
  id: ParcelLayerItem["id"];

  kind: ClassificationKind;

  /** Whether this parcel is a street parcel. **/
  isStreetParcel: boolean;

  /** The probability that this parcel is a street parcel, or `null` if it wasn't a candidate. */
  probability: number | null;

  /** The computed model features, or `null` when the parcel was never a candidate. */
  features: StreetParcelFeatures | null;

  /**
   * The IDs of demands that lie inside this parcel.
   *
   * This field is only populated for parcels whose probability is at least `minProbability`.
   */
  demandIds: Array<number | string>;
}

export type LayerClassificationResult =
  | (ClassificationResultBase & {
      kind: "distant";

      /** Distance (metres) from the parcel to the nearest street, or `null` if none lies within `nearestStreetSearchMetres`. */
      nearestStreetMetres: number | null;
    })
  | (ClassificationResultBase & { kind: Exclude<ClassificationKind, "distant"> });

const NOT_A_CANDIDATE = { isStreetParcel: false, probability: null, features: null };

/** A parcel whose area lies more than this fraction outside the design's range is clipped to it. */
const CLIP_AREA_TOLERANCE = 1e-3;

interface IndexedStreet {
  geometry: StreetGeometry;
  highway: string | null;
}

interface IndexedDemand {
  id: number | string;
}

/** Return whether the street comes within `gate` planar degrees of the given parcel. */
const withinGate = (street: StreetGeometry, parcel: Polygon | MultiPolygon, gate: number): boolean => lineWithinDegrees(parcel, street, gate);

/** Spatial index over a street layer. */
type StreetIndex = ReturnType<typeof makeIndex<LineString | MultiLineString, IndexedStreet>>;

/** Return an R-Tree containing all the streets. */
function buildStreetIndex(streets: ReadonlyArray<StreetLayerItem>): StreetIndex {
  const index = makeIndex<LineString | MultiLineString, IndexedStreet>();
  index.load({
    type: "FeatureCollection",
    features: streets.map((s) => asFeature(s.geometry, { geometry: s.geometry, highway: s.highway })),
  });
  return index;
}

/** Spatial index over the demand (address) points. */
type DemandIndex = ReturnType<typeof makeIndex<Point, IndexedDemand>>;

/** Return an R-Tree containing all of the demands. **/
function buildDemandIndex(demands: ReadonlyArray<DemandPoint>): DemandIndex {
  const index = makeIndex<Point, IndexedDemand>();
  index.load({ type: "FeatureCollection", features: demands.map((d) => asFeature(d.geometry, { id: d.id })) });
  return index;
}

/** Return the IDs of all demand points that fall inside the given parcel geometry. **/
function demandsInParcel(parcel: Polygon | MultiPolygon, parcelBbox: BBox, demandIndex: DemandIndex): Array<number | string> {
  const hits = demandIndex.search(parcelBbox);
  const ids: Array<number | string> = [];
  for (const f of hits.features as Array<Feature<Point, IndexedDemand>>) {
    if (booleanPointInPolygon(f.geometry, parcel)) ids.push(f.properties.id);
  }
  return ids;
}

/**
 * Find demand features inside each parcel using a spatial index.
 *
 * Builds an R-tree over the demand points once, then queries it for each parcel.
 * Returns a map from parcel feature id to the demand features that fall inside it.
 */
export function findDemandsForParcels(
  parcels: ReadonlyArray<Feature<Polygon | MultiPolygon>>,
  demandFeatures: ReadonlyArray<Feature<Point>>
): Map<Feature["id"], Feature<Point>[]> {
  const demandPoints: DemandPoint[] = demandFeatures.map((f, i) => ({
    id: f.id ?? `demand-${i}`,
    geometry: f.geometry,
  }));
  const demandIndex = buildDemandIndex(demandPoints);
  const demandById = new Map<number | string, Feature<Point>>();
  demandFeatures.forEach((f, i) => demandById.set(f.id ?? `demand-${i}`, f));

  const result = new Map<Feature["id"], Feature<Point>[]>();
  for (const parcel of parcels) {
    const parcelBbox = bbox(parcel.geometry);
    const ids = demandsInParcel(parcel.geometry, parcelBbox, demandIndex);
    result.set(
      parcel.id,
      ids.flatMap((id) => demandById.get(id) ?? [])
    );
  }
  return result;
}

/** Return the streets whose bounding boxes intersect the given bbox. */
const searchStreets = (streetIndex: StreetIndex, searchBbox: BBox): IndexedStreet[] =>
  streetIndex.search(searchBbox).features.map((f: Feature<LineString | MultiLineString, IndexedStreet>) => f.properties);

/** Return whether every street is at least `metres` from the parcel. */
function isFarFromStreets(parcel: Polygon | MultiPolygon, parcelBbox: BBox, streetIndex: StreetIndex, metres: number): boolean {
  return searchStreets(streetIndex, expandBboxMetres(parcelBbox, metres)).every((street) => !lineWithinMetres(parcel, street.geometry, metres));
}

/**
 * Return the distance (metres) from the parcel to the nearest street, or `null` if no street lies within
 * `searchMetres` of it.
 *
 * The parcel is assumed not to intersect any street.
 */
function nearestStreetMetres(parcel: Polygon | MultiPolygon, parcelBbox: BBox, streetIndex: StreetIndex, searchMetres: number): number | null {
  const streets = searchStreets(streetIndex, expandBboxMetres(parcelBbox, searchMetres)).map((s) => s.geometry);
  return nearestLineWithinMetres(parcel, streets, searchMetres);
}

/** The parcel geometry to classify once the parcel has been restricted to the design's range. */
type RangeRestriction = { kind: "model" | "clipped"; geometry: Polygon | MultiPolygon };

/**
 * Restrict the parcel to the design's range, i.e. the union of the streets' `rangeBuffers`.
 *
 * A parcel extending beyond the range is clipped to it, so that the model only considers the part of the
 * parcel that is near streets.
 */
function restrictToRange(
  parcel: Polygon | MultiPolygon,
  parcelBbox: BBox,
  rangeStreets: ReadonlyArray<StreetGeometry>,
  rangeBuffers: StreetBuffers
): RangeRestriction {
  const unrestricted: RangeRestriction = { kind: "model", geometry: parcel };

  // every point of the parcel lies within half the bbox diagonal of the bbox centre, so if the centre is within
  // the remaining distance of a street the whole parcel is in range and the (expensive) clip can be skipped.
  const [minX, minY, maxX, maxY] = parcelBbox;
  const halfDiagonal = distance([minX, minY], [maxX, maxY], { units: "meters" }) / 2;
  const remaining = rangeBuffers.metres - halfDiagonal;
  if (remaining > 0 && pointWithinMetres([(minX + maxX) / 2, (minY + maxY) / 2], rangeStreets, remaining)) return unrestricted;

  const range = rangeBuffers.union(rangeStreets);
  const clipped = range ? intersect(featureCollection([asFeature(parcel), range])) : null;
  if (!clipped || area(clipped) >= area(parcel) * (1 - CLIP_AREA_TOLERANCE)) return unrestricted;
  return { kind: "clipped", geometry: clipped.geometry };
}

/**
 * Classify a single parcel.
 *
 * The parcel passes through a sequence of increasingly expensive stages, each of which may finish the
 * classification early:
 *
 * 1. Distant: a parcel with no demand inside it and no street within `distantDistanceMetres` is far from the
 *    design; only its nearest-street distance is reported.
 * 2. Gate: a parcel with no non-service street within `gateDistanceDegrees` is excluded.
 * 3. Range: a parcel extending beyond the streets' `rangeBufferMetres` buffers is clipped to them.
 * 4. Candidate: the model features are computed for the (possibly clipped) geometry, and a parcel with too little
 *    street overlap and centreline is excluded.
 * 5. Model: the remaining parcels are scored.
 */
function classifyAgainstIndex(
  parcel: ParcelLayerItem,
  streetIndex: StreetIndex,
  demandIndex: DemandIndex,
  rangeBuffers: StreetBuffers,
  model: CompactModel,
  config: LayerClassificationConfig
): LayerClassificationResult {
  const parcelBbox = bbox(parcel.geometry);
  const demandIds = demandsInParcel(parcel.geometry, parcelBbox, demandIndex);

  if (demandIds.length === 0 && isFarFromStreets(parcel.geometry, parcelBbox, streetIndex, config.distantDistanceMetres)) {
    const nearest = nearestStreetMetres(parcel.geometry, parcelBbox, streetIndex, config.nearestStreetSearchMetres);
    return { id: parcel.id, kind: "distant", ...NOT_A_CANDIDATE, demandIds: [], nearestStreetMetres: nearest };
  }

  const nearby = searchStreets(streetIndex, expandBboxDegrees(parcelBbox, config.nearbyRadiusDegrees));

  // the gate is judged against the parcel's full geometry; only the model sees the range-restricted geometry
  const gateStreets = nearby
    .filter((s) => s.highway !== config.serviceHighwayValue && withinGate(s.geometry, parcel.geometry, config.gateDistanceDegrees))
    .map((s) => s.geometry);
  if (gateStreets.length === 0) return { id: parcel.id, kind: "excluded", ...NOT_A_CANDIDATE, demandIds: [] };

  const rangeStreets = searchStreets(streetIndex, expandBboxMetres(parcelBbox, rangeBuffers.metres)).map((s) => s.geometry);
  const range = restrictToRange(parcel.geometry, parcelBbox, rangeStreets, rangeBuffers);

  // service streets don't gate a parcel but still count towards its centreline length and centroid distance
  const features = computeStreetParcelFeatures(
    { geometry: range.geometry, addressType: parcel.addressType, gateStreets, nearbyStreets: nearby.map((s) => s.geometry) },
    config
  );
  const isCandidate =
    (features.street_overlap_ratio ?? 0) >= config.minStreetOverlapRatio || features.centerline_len_m >= config.minCenterlineLengthMetres;
  if (!isCandidate) return { id: parcel.id, kind: "excluded", ...NOT_A_CANDIDATE, demandIds: [] };

  const probability = predictProbability(features, model);
  const isStreetParcel = probability >= model.threshold;
  const minProbability = config.minProbability ?? model.threshold;
  return { id: parcel.id, kind: range.kind, isStreetParcel, probability, features, demandIds: probability >= minProbability ? demandIds : [] };
}

export function classifyParcelLayer(
  parcels: ReadonlyArray<ParcelLayerItem>,
  streets: ReadonlyArray<StreetLayerItem>,
  demands: ReadonlyArray<DemandPoint>,
  model: CompactModel,
  config: LayerClassificationConfig = DEFAULT_LAYER_CLASSIFICATION_CONFIG,
  onProgress?: (completed: number, total: number) => void
): LayerClassificationResult[] {
  const streetIndex = buildStreetIndex(streets);
  const demandIndex = buildDemandIndex(demands);
  const rangeBuffers = new StreetBuffers(config.rangeBufferMetres);
  const total = parcels.length;
  const reportEvery = Math.max(1, Math.floor(total / 100)); // at most ~100 progress callbacks
  const results: LayerClassificationResult[] = [];
  for (let i = 0; i < total; i++) {
    results.push(classifyAgainstIndex(parcels[i], streetIndex, demandIndex, rangeBuffers, model, config));
    if (onProgress && (i + 1 === total || (i + 1) % reportEvery === 0)) onProgress(i + 1, total);
  }
  return results;
}
